import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, Package, Edit3, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";

export default function Products() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "management", "general_manager"].includes(user?.role);
  const [tab, setTab] = useState("models");
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/product-categories").then(r => setCategories(r.data));
    api.get("/products").then(r => setModels(r.data));
  };
  useEffect(() => { load(); }, []);

  const delItem = async (kind, id) => {
    if (!window.confirm(kind === "model" ? "Archive this model?" : "Delete this category?")) return;
    const path = kind === "model" ? `/products/${id}` : `/product-categories/${id}`;
    try { await api.delete(path); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Product Catalogue</h1>
          <p className="text-slate-600 mt-1">Category → Model → Available Sizes. Sizes are used only for POS calculations and floor-stock records.</p>
        </div>
        {canEdit && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="mlily-btn h-11 px-5 rounded-lg font-semibold text-sm flex items-center gap-2" data-testid="new-product-btn">
            <Plus className="w-4 h-4" /> New {tab === "categories" ? "Category" : "Model"}
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b">
        <TabBtn active={tab === "models"} onClick={() => setTab("models")} testid="tab-models"><Package className="w-4 h-4" /> Models ({models.length})</TabBtn>
        <TabBtn active={tab === "categories"} onClick={() => setTab("categories")} testid="tab-categories"><Layers className="w-4 h-4" /> Categories ({categories.length})</TabBtn>
      </div>

      {tab === "models" ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Model</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-left px-4 py-3">Sizes</th>
                <th className="text-left px-4 py-3">Status</th>
                {canEdit && <th></th>}
              </tr>
            </thead>
            <tbody>
              {models.map(m => (
                <tr key={m.id} className="border-t hover:bg-slate-50" data-testid={`product-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium">{m.name}</td>
                  <td className="px-4 py-3">{m.category_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">{m.sku || "—"}</td>
                  <td className="px-4 py-3 text-xs">{(m.available_sizes || []).join(", ") || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${m.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{m.active ? "Active" : "Archived"}</span></td>
                  {canEdit && <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => { setEditing(m); setShowForm(true); }} className="p-1.5 text-slate-500 hover:text-[#003DA5]" data-testid={`edit-product-${m.id}`}><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => delItem("model", m.id)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`delete-product-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>}
                </tr>
              ))}
              {models.length === 0 && <tr><td colSpan={canEdit ? 6 : 5} className="px-4 py-12 text-center text-slate-500">No models yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">Status</th>
                {canEdit && <th></th>}
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`category-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-slate-600">{c.description || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Active" : "Inactive"}</span></td>
                  {canEdit && <td className="px-4 py-3 text-right"><button onClick={() => delItem("category", c.id)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`delete-category-${c.id}`}><Trash2 className="w-4 h-4" /></button></td>}
                </tr>
              ))}
              {categories.length === 0 && <tr><td colSpan={canEdit ? 4 : 3} className="px-4 py-12 text-center text-slate-500">No categories yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (tab === "models"
        ? <ModelForm initial={editing} categories={categories} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
        : <CategoryForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />)}
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-3 -mb-px border-b-2 text-sm font-semibold flex items-center gap-2 ${active ? "border-[#003DA5] text-[#003DA5]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
    {children}
  </button>
);

function ModelForm({ initial, categories, onClose, onSaved }) {
  const [f, setF] = useState(initial ? {
    ...initial,
    available_sizes_str: (initial.available_sizes || []).join(", "),
  } : { name: "", category_id: categories[0]?.id || "", sku: "", description: "", active: true, available_sizes_str: "" });
  const submit = async (e) => {
    e.preventDefault();
    const body = {
      name: f.name, category_id: f.category_id, sku: f.sku, description: f.description, active: f.active,
      available_sizes: f.available_sizes_str.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (initial?.id) await api.patch(`/products/${initial.id}`, body);
      else await api.post("/products", body);
      toast.success(initial ? "Model updated" : "Model created"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg">
        <div className="p-5 border-b flex justify-between"><h3 className="text-xl font-heading font-semibold">{initial ? "Edit Model" : "New Model"}</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <F label="Name *"><input required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="product-name-input" /></F>
          <F label="Category *"><select required value={f.category_id} onChange={e => setF({ ...f, category_id: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="product-category-input">
            <option value="">— select —</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></F>
          <F label="SKU"><input value={f.sku || ""} onChange={e => setF({ ...f, sku: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="product-sku-input" /></F>
          <F label="Available sizes (comma-separated)"><input value={f.available_sizes_str} onChange={e => setF({ ...f, available_sizes_str: e.target.value })} placeholder="Queen, King, Long Single" className="w-full h-11 px-3 border rounded-lg" data-testid="product-sizes-input" /></F>
          <F label="Description"><textarea value={f.description || ""} onChange={e => setF({ ...f, description: e.target.value })} rows={2} className="w-full p-3 border rounded-lg" /></F>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.active} onChange={e => setF({ ...f, active: e.target.checked })} /> Active</label>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button><button type="submit" className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="save-product-btn">Save</button></div>
      </form>
    </div>
  );
}

function CategoryForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", description: "", active: true });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/product-categories", f); toast.success("Category created"); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-md">
        <div className="p-5 border-b flex justify-between"><h3 className="text-xl font-heading font-semibold">New Category</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <F label="Name *"><input required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="category-name-input" /></F>
          <F label="Description"><textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} rows={2} className="w-full p-3 border rounded-lg" /></F>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button><button type="submit" className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="save-category-btn">Save</button></div>
      </form>
    </div>
  );
}

const F = ({ label, children }) => <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label><div className="mt-1">{children}</div></div>;
